// Die Doku-App, nach dem Mockup (Vorlage für Stufe 1). Seit E2 mit der
// Besichtigung draußen; welche Seite offen ist, steht hinter dem # in der
// Adresse:
//
//   #/                                  Startseite
//   #/neu                               Neue Besichtigung
//   #/aufnahme/<kennung>                die Stationen einer Besichtigung
//   #/aufnahme/<kennung>/station/<nr>   eine Station (1 bis 7)
//   …/station/<nr>/foto/<kennung>       ein Foto groß
//   …/station/<nr>/foto/<kennung>/zeichnen   auf dem Foto zeichnen
//   …/station/<nr>/material             Material aus dieser Station (seit E3b)
//   #/aufnahme/<kennung>/material       Material am Schluss, nach Station 7 (seit E3b)
//   #/aufnahme/<kennung>/k2             die Dachplanung mit K2 (seit Schritt 3)
//   #/aufnahme/<kennung>/kunde          Name und Adresse berichtigen (seit 19.10.)
//   #/aufnahme/<kennung>/senden         Abschließen und senden (seit Schritt 4)
//   #/aufnahme/<kennung>/gesendet       „Ist im Büro!“ – wie es nach dem Senden steht
//   #/aufnahme/<kennung>/nachtrag       „Nachtrag senden“ – was nachgereicht wird (seit Schritt 5)
//   …/nachtrag/gesendet                 „Nachtrag ist im Büro!“
//
// Die Technik-Proben aus E1 (#/proben) gibt es seit E2, Schritt 6 nicht
// mehr – alles, was sie geprüft haben, kann die App jetzt selbst. Ihre
// Datenbank am Gerät räumt der Start einmal weg (speicher.ts).
//
// Ist eine Besichtigung gesendet, zeigen Übersicht, Station und Foto sie nur
// zum Lesen (seit Schritt 4) – dieselben Adressen, ein anderer Modus. Läuft
// „Etwas nachreichen“ (seit Schritt 5), ebenso: dieselben Seiten im Modus
// Nachtrag (arbeitskopie.ts, `bearbeiten`).
//
// Hier startet auch der Sender, der den Postausgang an den Server bringt,
// und einmal beim Start: der Umzug alter Fotos und Memos in „dateien“
// (dateien.ts, seit 19.10.2026), dann die Rettung abgebrochener
// Sprachmemos (memo.ts); daneben verschwindet die alte Proben-Datenbank.
//
// Seit E3 (Rollen, Gernots Entscheidung 3): Die Doku-App ist für den Verkauf
// – die Leitung darf sie auch (Manuel verkauft selbst; `darfDoku` im Kern).
// Wer mit einer anderen Rolle angemeldet ist (Büro, Montage …), sieht
// gleich nach der Anmeldung einen ruhigen Bildschirm mit „abmelden“ – statt
// bei jedem Eintrag einen Fehler (der Server weist ihn ohnehin mit 403 ab).
// Der Sender startet dann gar nicht erst.
//
// Seit E3c (S5; O86): Was am Gerät für die Firma liegt – die Standardliste
// zum Antippen und die Adresse von K2 –, gehört zu der Firma, für die es
// geholt wurde. Beim Start prüft die App, ob es die Firma der Anmeldung ist
// (sonst kommt es weg), und beim Abmelden kommt es ganz weg (material.ts).

import { useEffect } from "react";
import { darfDoku, type IchAntwort } from "@happy-energy/kern";
import { fehlerMelden, TestBand, Toast, useSeite, type AnmeldungHilfen } from "@happy-energy/oberflaeche";
import { probenSpeicherWegraeumen } from "./speicher.ts";
import { firmaPruefen, firmaVergessen } from "./material.ts";
import { sender } from "./sender.ts";
import { memosRettenBeimStart } from "./memo.ts";
import { dateienUmziehen } from "./dateien.ts";
import { NeueBesichtigung } from "./besichtigung/NeueBesichtigung.tsx";
import { StationSeite } from "./besichtigung/StationSeite.tsx";
import { Startseite } from "./besichtigung/Startseite.tsx";
import { Uebersicht } from "./besichtigung/Uebersicht.tsx";
import { FotoSeite } from "./besichtigung/FotoSeite.tsx";
import { ZeichnenSeite } from "./besichtigung/ZeichnenSeite.tsx";
import { K2Seite } from "./besichtigung/K2Seite.tsx";
import { KundeAendern } from "./besichtigung/KundeAendern.tsx";
import { SendenSeite } from "./besichtigung/SendenSeite.tsx";
import { GesendetSeite } from "./besichtigung/GesendetSeite.tsx";
import { NachtragSeite } from "./besichtigung/NachtragSeite.tsx";
import { MaterialSeite } from "./besichtigung/MaterialSeite.tsx";
import "./doku.css";

const AUFNAHME_SEITE =
  /^\/aufnahme\/([0-9a-f-]{36})(?:\/(k2|kunde|senden|gesendet|nachtrag\/gesendet|nachtrag|material)|\/station\/([1-9])(?:\/(material)|\/foto\/([0-9a-f-]{36})(\/zeichnen)?)?)?\/?$/;

function Seite({ ich, hilfen }: { ich: IchAntwort; hilfen: AnmeldungHilfen }) {
  const seite = useSeite();

  // Neue Seite: oben anfangen (sonst bleibt man beim Weitergehen unten stehen).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [seite]);

  if (seite === "/neu") return <NeueBesichtigung ich={ich} />;
  const treffer = AUFNAHME_SEITE.exec(seite);
  if (treffer) {
    const [, aufnahmeId, teil, nr, stationMaterial, fotoId, zeichnen] = treffer;
    if (teil === "material") return <MaterialSeite ich={ich} aufnahmeId={aufnahmeId!} />;
    if (nr && stationMaterial) return <MaterialSeite ich={ich} aufnahmeId={aufnahmeId!} nr={Number(nr)} />;
    if (teil === "k2") return <K2Seite ich={ich} aufnahmeId={aufnahmeId!} />;
    if (teil === "kunde") return <KundeAendern ich={ich} aufnahmeId={aufnahmeId!} />;
    if (teil === "senden") return <SendenSeite ich={ich} aufnahmeId={aufnahmeId!} />;
    if (teil === "gesendet") return <GesendetSeite ich={ich} aufnahmeId={aufnahmeId!} />;
    if (teil === "nachtrag") return <NachtragSeite ich={ich} aufnahmeId={aufnahmeId!} />;
    if (teil === "nachtrag/gesendet") return <GesendetSeite ich={ich} aufnahmeId={aufnahmeId!} nachtrag />;
    if (nr && fotoId) {
      return zeichnen ? (
        <ZeichnenSeite ich={ich} aufnahmeId={aufnahmeId!} nr={Number(nr)} fotoId={fotoId} />
      ) : (
        <FotoSeite ich={ich} aufnahmeId={aufnahmeId!} nr={Number(nr)} fotoId={fotoId} />
      );
    }
    return nr ? <StationSeite ich={ich} aufnahmeId={aufnahmeId!} nr={Number(nr)} /> : <Uebersicht ich={ich} aufnahmeId={aufnahmeId!} />;
  }
  return <Startseite ich={ich} abmelden={hilfen.abmelden} />;
}

/**
 * Angemeldet mit einer Rolle, die hier nicht arbeitet: ruhig sagen, wo es
 * für sie weitergeht – und abmelden (etwa, damit sich auf diesem Handy der
 * Verkäufer anmelden kann). Im Stil der Anmeldung.
 */
function FalscheRolle({ ich, abmelden }: { ich: IchAntwort; abmelden: () => void }) {
  return (
    <>
      <TestBand umgebung={ich.umgebung} />
      <div className="anmeldung falsche-rolle">
        <div className="anmeldung-kasten">
          <img src="/symbol-192.png" alt="" className="anmeldung-zeichen" />
          <h1>
            Happy Energy <span>Doku</span>
          </h1>
          <p className="gross">Hallo {ich.nutzer.name.split(" ")[0]}!</p>
          <p className="leise">Die Doku-App ist für den Verkauf. Im Büro geht alles im CRM am Laptop.</p>
          <button type="button" className="zweit dunkel" onClick={abmelden}>
            abmelden
          </button>
          <p className="klein-text">Angemeldet als {ich.nutzer.name}</p>
        </div>
      </div>
    </>
  );
}

export function Doku({ ich, hilfen }: { ich: IchAntwort; hilfen: AnmeldungHilfen }) {
  // O86 (seit E3c, S5): Beim Abmelden zuerst, was am Gerät für die Firma liegt (Standardliste, Adresse von K2) –
  // geht das schief, wird trotzdem abgemeldet; der nächste Start prüft die Firma ohnehin.
  const abmelden = () => {
    void firmaVergessen()
      .catch((fehler: unknown) => fehlerMelden("doku", fehler))
      .finally(() => hilfen.abmelden());
  };
  if (!darfDoku(ich.nutzer.rolle)) return <FalscheRolle ich={ich} abmelden={abmelden} />;
  return <DokuApp ich={ich} hilfen={{ ...hilfen, abmelden }} />;
}

function DokuApp({ ich, hilfen }: { ich: IchAntwort; hilfen: AnmeldungHilfen }) {
  // Der Sender läuft, solange jemand angemeldet ist. Sagt der Server
  // „nicht angemeldet“, fragt die Anmeldung neu – der Postausgang bleibt.
  const { neuFragen } = hilfen;
  useEffect(() => {
    sender.einstellen({ anmeldungNoetig: neuFragen });
    return sender.starten();
  }, [neuFragen]);

  // O86 (seit E3c, S5): Gehört, was am Gerät für eine Firma liegt, zu dieser Anmeldung? Sonst kommt es weg.
  const firmaId = ich.firma.id;
  useEffect(() => {
    firmaPruefen(firmaId).catch((fehler: unknown) => fehlerMelden("doku", fehler));
  }, [firmaId]);

  // Alte Fotos und Memos ziehen um (dateien.ts); brach eine Aufnahme ab (Absturz, App geschlossen),
  // wird aus ihren Teilstücken danach das Memo.
  useEffect(() => {
    void dateienUmziehen()
      .catch((fehler: unknown) => fehlerMelden("doku", fehler))
      .then(() => memosRettenBeimStart());
    probenSpeicherWegraeumen().catch((fehler: unknown) => fehlerMelden("doku", fehler));
  }, []);

  return (
    <>
      <Seite ich={ich} hilfen={hilfen} />
      <Toast />
    </>
  );
}
